import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Icon, type HeroiconComponent } from '@/components/foundation/Icon';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'destructive' | 'icon';
/** @deprecated Use `destructive` instead. Kept for backward compatibility. */
export type ButtonVariantLegacy = ButtonVariant | 'danger';

export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariantLegacy;
  size?: ButtonSize;
  loading?: boolean;
  icon?: HeroiconComponent;
  iconPosition?: 'left' | 'right';
  children?: ReactNode;
}

const sizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 px-3 text-sm gap-1.5',
  md: 'h-10 px-4 text-sm gap-2',
  lg: 'h-12 px-5 text-base gap-2',
};

const iconSizeClasses: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 min-h-8 min-w-8 p-0',
  md: 'h-11 w-11 min-h-[44px] min-w-[44px] p-0',
  lg: 'h-12 w-12 min-h-12 min-w-12 p-0',
};

function resolveVariant(variant: ButtonVariantLegacy = 'primary'): ButtonVariant {
  return variant === 'danger' ? 'destructive' : variant;
}

function variantClasses(variant: ButtonVariant): string {
  switch (variant) {
    case 'primary':
      return 'bg-gradient-to-r from-[var(--color-brand-primary)] to-[var(--color-brand-ai)] text-white font-semibold hover:opacity-90 hover:shadow-[0_4px_12px_rgba(99,102,241,0.4)]';
    case 'secondary':
      return 'border border-[var(--border-visible)] bg-[var(--bg-card)] text-[var(--text-primary)] hover:bg-[var(--bg-card-hover)]';
    case 'ghost':
      return 'border border-transparent bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] hover:text-[var(--text-primary)]';
    case 'destructive':
      return 'bg-[var(--status-danger)] text-white hover:bg-[var(--status-danger)]/90 hover:shadow-[0_4px_12px_rgba(239,68,68,0.4)]';
    case 'icon':
      return 'border border-transparent bg-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-card-hover)] hover:text-[var(--text-primary)]';
  }
}

function ButtonSpinner() {
  return (
    <span
      className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
      aria-hidden="true"
    />
  );
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    iconPosition = 'left',
    className,
    children,
    disabled,
    type = 'button',
    ...props
  },
  ref,
) {
  const resolved = resolveVariant(variant);
  const isIconOnly = resolved === 'icon' || (!children && icon);
  const isDisabled = disabled || loading;

  return (
    <button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-[var(--transition-fast)] ease-[var(--ease-out-cubic)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg-card)] disabled:cursor-not-allowed disabled:opacity-60',
        variantClasses(resolved),
        isIconOnly ? iconSizeClasses[size] : sizeClasses[size],
        className,
      )}
      {...props}
    >
      {loading ? <ButtonSpinner /> : null}
      {!loading && icon && iconPosition === 'left' ? (
        <Icon icon={icon} size={16} decorative />
      ) : null}
      {children}
      {!loading && icon && iconPosition === 'right' ? (
        <Icon icon={icon} size={16} decorative />
      ) : null}
    </button>
  );
});

Button.displayName = 'Button';